import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Typography,
  Chip,
  Divider,
  Stack,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  ConfirmationNumber as TicketIcon,
  LocationOn as LocationIcon,
  Event as EventIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import type { MyEventTicket } from '../../api/events.api';

interface TicketModalProps {
  open: boolean;
  onClose: () => void;
  ticket: MyEventTicket;
  eventTitle: string;
  /** Preformatted, display-ready date/time string (page owns the formatting). */
  eventDateLabel: string;
  venueLine: string;
}

/**
 * Full-screen-friendly ticket display for parents to show at the event entrance.
 *
 * The QR encodes ONLY the raw `ticket.qrToken` — that is exactly what the
 * check-in scanner resolves against (the backend QR/PDF generators encode the
 * same token), so a screen-displayed QR scans identically to a printed one.
 *
 * Brand: primary blue #4580E4, mint #96DAC9. Flat — no shadows/gradients. The
 * QR sits on a white block with dark modules for reliable phone-screen scanning.
 */
export default function TicketModal({
  open,
  onClose,
  ticket,
  eventTitle,
  eventDateLabel,
  venueLine,
}: TicketModalProps) {
  const tierLabel = ticket.EventTicketTier?.label ?? 'Ticket';
  const isCheckedIn = ticket.status === 'CHECKED_IN';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      aria-label="Event ticket"
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TicketIcon sx={{ color: '#4580E4' }} />
          <Typography component="span" variant="h6" fontFamily="inherit">
            Your ticket
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close" size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} alignItems="center" sx={{ pt: 1 }}>
          {/* QR — white block, dark modules, high contrast for scanning */}
          <Box
            sx={{
              backgroundColor: '#ffffff',
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <QRCodeSVG
              value={ticket.qrToken}
              size={220}
              level="M"
              bgColor="#ffffff"
              fgColor="#080808"
            />
          </Box>

          <Typography variant="body2" color="text.secondary" textAlign="center">
            Show this QR code at the event entrance.
          </Typography>

          <Chip
            label={isCheckedIn ? 'Checked in' : 'Valid'}
            icon={isCheckedIn ? <CheckIcon /> : undefined}
            sx={{
              backgroundColor: isCheckedIn ? '#96DAC9' : '#4580E4',
              color: isCheckedIn ? '#080808' : '#ffffff',
              fontWeight: 600,
            }}
          />

          <Divider flexItem />

          {/* Ticket / event details */}
          <Stack spacing={1.25} sx={{ width: '100%' }}>
            <DetailRow icon={<TicketIcon fontSize="small" />} text={tierLabel} bold />
            <DetailRow icon={<EventIcon fontSize="small" />} text={eventTitle} bold />
            <DetailRow icon={<EventIcon fontSize="small" />} text={eventDateLabel} />
            {venueLine && <DetailRow icon={<LocationIcon fontSize="small" />} text={venueLine} />}
            <DetailRow icon={<PersonIcon fontSize="small" />} text={ticket.holderName} />
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

function DetailRow({ icon, text, bold }: { icon: React.ReactNode; text: string; bold?: boolean }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ color: 'text.secondary', display: 'flex' }}>{icon}</Box>
      <Typography variant="body2" fontWeight={bold ? 600 : 400}>
        {text}
      </Typography>
    </Box>
  );
}
